import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.jsx';

let offen = 0; // Anzahl offener Sheets (übereinander möglich)

/**
 * Bottom-Sheet auf dem Handy, zentrierter Dialog auf dem Desktop.
 * Wird direkt in <body> gehängt (nicht in den scrollenden Seitenbereich) und sperrt den Hintergrund – sonst nimmt
 * auf dem Handy der Bereich dahinter die Wischgeste, und langer Inhalt im Sheet lässt sich nicht scrollen.
 */
export function Sheet({ title, onClose, children }) {
  const titleId = useId();
  const ref = useRef(null);
  const wrapRef = useRef(null);

  // iPhone: Die Tastatur verkleinert nur den sichtbaren Bereich – das Sheet passt sich daran an, damit Felder und
  // Knöpfe über der Tastatur bleiben und das Sheet (nicht die Seite dahinter) scrollt.
  useEffect(() => {
    const vv = window.visualViewport;
    const wrap = wrapRef.current;
    if (!vv || !wrap) return undefined;
    const anpassen = () => {
      wrap.style.top = `${vv.offsetTop}px`;
      wrap.style.height = `${vv.height}px`;
      wrap.style.bottom = 'auto';
    };
    anpassen();
    vv.addEventListener('resize', anpassen);
    vv.addEventListener('scroll', anpassen);
    return () => { vv.removeEventListener('resize', anpassen); vv.removeEventListener('scroll', anpassen); };
  }, []);

  // Fokussiertes Feld im Sheet sichtbar halten (nach dem Aufgehen der Tastatur)
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const onFocus = (e) => {
      if (!/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      setTimeout(() => e.target.scrollIntoView?.({ block: 'nearest' }), 300);
    };
    node.addEventListener('focusin', onFocus);
    return () => node.removeEventListener('focusin', onFocus);
  }, []);

  useEffect(() => {
    offen++;
    document.body.classList.add('sheet-offen');
    return () => { if (--offen === 0) document.body.classList.remove('sheet-offen'); };
  }, []);

  useEffect(() => {
    const prev = document.activeElement;
    ref.current?.querySelector('input, textarea, select, button:not([data-close])')?.focus({ preventScroll: true });
    // Esc schliesst; Tab bleibt im Sheet (Fokus geht nicht hinter das Sheet, WCAG 2.4.3 / 2.4.11)
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !ref.current) return;
      const ziele = [...ref.current.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null);
      if (!ziele.length) return;
      const erstes = ziele[0];
      const letztes = ziele[ziele.length - 1];
      if (e.shiftKey && document.activeElement === erstes) { e.preventDefault(); letztes.focus(); }
      else if (!e.shiftKey && document.activeElement === letztes) { e.preventDefault(); erstes.focus(); }
    };
    ref.current?.addEventListener('keydown', onKey);
    const node = ref.current;
    return () => {
      node?.removeEventListener('keydown', onKey);
      prev?.focus?.({ preventScroll: true });
    };
  }, [onClose]);

  return createPortal(
    <div className="sheetwrap" ref={wrapRef}>
      <button className="backdrop" aria-label="Schliessen" data-close onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="grabber" />
        <div className="between">
          <h2 id={titleId} className="h2" style={{ fontSize: 21 }}>{title}</h2>
          <button className="iconbtn" style={{ borderRadius: 24 }} aria-label="Schliessen" data-close onClick={onClose}>
            <Icon name="x" size={20} stroke={2.4} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
